/**
 * 계약 시설 관련 API 훅
 * 계약의 시설 정보를 관리하고 운영하는 기능을 제공하는 커스텀 훅입니다.
 *
 * 주요 기능:
 * - 운영 정보 조회 및 수정
 * - 공용 시설 관리
 * - 요금 규칙 관리
 * - 시설 사용 현황 조회
 */

import { useCallback } from "react";
import {
  addNUpdateOperationData,
  deleteOperationFacility,
  editContractOperation,
  getBaseFacilityPricies,
  getContractFacilityList,
  getContractOperationInfo,
  getFacilityPricieSetting,
  getFacilityUsage,
  getProductFacilityList,
  setFacilityPriceSetting,
} from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";

/**
 * 계약 시설 관련 API 훅
 * 시설 정보 관리와 운영을 위한 다양한 API 호출 함수를 제공합니다.
 */
const useContractFacilityApi = () => {
  // API 호출 함수 정의
  /** 운영정보 상세 조회 API */
  const { executeAsync: getOperationInfo } = useApiOperation(getContractOperationInfo);
  /** 운영정보 수정 API */
  const { executeAsync: editOperationInfo } = useApiOperation(editContractOperation);
  /** 운영정보 공용 시설 조회 API */
  const { executeAsync: getOperationFacilities } = useApiOperation(getContractFacilityList);
  /** 공용 시설 부가 정보 조회 API */
  const { executeAsync: getFacilitiesDetatils } = useApiOperation(getProductFacilityList);
  /** 운영정보 공용 시설 삭제 API */
  const { executeAsync: deleteFacility } = useApiOperation(deleteOperationFacility);
  /** 운영정보 공용 시설 등록/수정 API */
  const { executeAsync: addNUpdateFacility } = useApiOperation(addNUpdateOperationData);
  /** 프로바이더 요금 룰 목록 조회 API */
  const { executeAsync: getBaseRules } = useApiOperation(getBaseFacilityPricies);
  /** 건물- 공용 공간 요금 룰 등록/수정/삭제 API */
  const { executeAsync: setSettingPrice } = useApiOperation(setFacilityPriceSetting);
  /** 건물- 공용 공간 요금 룰 목록 조회 API */
  const { executeAsync: getSettingPrice } = useApiOperation(getFacilityPricieSetting);
  /** 월별 공용 공간 사용 실정 조회 API */
  const { executeAsync: getUsage } = useApiOperation(getFacilityUsage);

  /**
   * 운영정보 상세 조회
   * @param contractId - 계약 ID
   * @returns 운영 정보 상세 데이터
   */
  const operationInfoData = useCallback(
    async (contractId: string) => {
      const response: any = await getOperationInfo({ id: Number(contractId) });

      if (response.data.data.content) {
        return response.data.data.content;
      } else {
        return {};
      }
    },
    [getOperationInfo],
  );

  /**
   * 운영 시설 목록 조회
   * @param contractId - 계약 ID
   * @returns 운영 시설 목록 데이터
   */
  const operationsFacilities = useCallback(
    async (contractId: string) => {
      const response: any = await getOperationFacilities({ id: Number(contractId) });

      if (response.data.data.content) {
        return response.data.data.content;
      } else {
        return {};
      }
    },
    [getOperationFacilities],
  );

  /**
   * 상품 시설 정보 조회
   * @param productId - 상품 ID
   * @param idList - 시설 ID 목록
   * @returns 시설 상세 정보 목록
   */
  const getPrFacilities = useCallback(
    async (productId: string, idList: String) => {
      const response: any = await getFacilitiesDetatils({ productId, buildlingCommonFacilityId: idList, size: 9999 });

      if (response.data.data.content) {
        return response.data.data.content;
      } else {
        return [];
      }
    },
    [getFacilitiesDetatils],
  );

  /**
   * 시설 삭제
   * @param contractId - 계약 ID
   * @param facilityId - 시설 ID
   * @returns API 응답 결과
   */
  const delFacility = useCallback(
    async (contractId: string, facilityId: string) => {
      const response = await deleteFacility({ id: Number(contractId), facilityId: Number(facilityId) });
      return response;
    },
    [deleteFacility],
  );

  /**
   * 시설 정보 등록 또는 수정
   * @param saveData - 저장할 시설 데이터
   * @returns 저장 성공 여부
   */
  const insertOrUpdate = useCallback(
    async (saveData: any) => {
      await addNUpdateFacility(saveData);
      return true;
    },
    [addNUpdateFacility],
  );

  /**
   * 시설 사용 현황 조회
   * @param contractId - 계약 ID
   * @param type - 시설 타입
   * @param month - 조회 월 배열
   * @returns 시설 사용 현황 데이터
   */
  const searchFacilityUsage = useCallback(
    async (contractId: string, type: string, month: Array<string>) => {
      const response = await getUsage({ id: Number(contractId), facilityType: type, yearMonth: month });
      return response.data.data.facilityUsage;
    },
    [getUsage],
  );

  /**
   * 프로바이더 요금 규칙 조회
   * @param providerId - 프로바이더 ID
   * @param buildingCommonFacilityId - 공용 시설 ID
   * @returns 요금 규칙 목록
   */
  const getProviderPriceRules = useCallback(
    async (providerId: string, buildingCommonFacilityId: string) => {
      const response = await getBaseRules({ providerId, page: 0, size: 9999 });

      return response.data.data.content || [];
    },
    [getBaseRules],
  );

  /**
   * 계약 요금 규칙 조회
   * @param serviceId - 서비스 ID
   * @param serviceType - 서비스 타입
   * @returns 요금 규칙 목록
   */
  const getContractPriceRules = useCallback(
    async (serviceId: string, serviceType: string) => {
      const response = await getSettingPrice({ serviceId, serviceType });
      return response.data.data.content || [];
    },
    [getSettingPrice],
  );

  /**
   * 계약 운영 정보 수정
   * @param saveData - 저장할 운영 정보 데이터
   * @returns API 응답 결과
   */
  const setContractOperation = useCallback(
    async (saveData: any) => {
      const response = await editOperationInfo(saveData);
      return response;
    },
    [editOperationInfo],
  );

  /**
   * 계약 요금 규칙 설정
   * @param buildingCommonFacilityPriceRuleList - 요금 규칙 목록
   * @returns 설정 성공 여부
   */
  const setContractPriceRules = useCallback(
    async (buildingCommonFacilityPriceRuleList: Array<any>) => {
      const response: any = await setSettingPrice({ buildingCommonFacilityPriceRuleList });
      return response.data.data.content.length > 0 ? true : false;
    },
    [setSettingPrice],
  );

  return {
    operationInfoData,
    operationsFacilities,
    getPrFacilities,
    delFacility,
    insertOrUpdate,
    searchFacilityUsage,
    getProviderPriceRules,
    getContractPriceRules,
    setContractOperation,
    setContractPriceRules,
  };
};

export default useContractFacilityApi;
